import { has } from 'lodash';
import {
  INIT_VIDEO_STREAM_SUCCESS,
  INIT_AUDIO_STREAM_SUCCESS,
  INIT_SCREEN_STREAM_SUCCESS,
  STOP_VIDEO_STREAM,
  STOP_AUDIO_STREAM,
  STOP_SCREEN_STREAM,
  SET_DEVICE_CONTROLLER,
  SET_MEETING_SESSION,
  STREAM_ERROR,
  END_MEETING_SESSION,
  INIT_DEVICES_SUCCESS,
  SET_SELECTED_DEVICE,
  OPEN_MEDIA_RECOVERY_MODAL,
  SET_SCREEN_SHARE_INITIATED,
  CLEAR_SCREEN_SHARE_INITIATED,
  VIDEO_TILE_DID_UPDATE,
  VIDEO_TILE_WAS_REMOVED,
  SET_LOCAL_VIDEO_ELEMENT,
  SET_LOCAL_MUTE,
  SET_REMOTE_MUTE,
  CLOSE_MEDIA_RECOVERY_MODAL,
  UPDATE_ATTENDEE,
  REMOVE_ATTENDEE,
  SET_WEBRTC_STREAM,
  SET_WEBRTC_SESSION_ID,
} from '../actions/InterviewRecordingActions';

const initialState = {
  videoStream: null,
  audioStream: null,
  screenStream: null,
  webrtcStream: null, // Added for NT interviews
  webrtcSessionId: null, // Added for NT interviews
  deviceController: null,
  meetingSession: null,
  error: null,
  hasVideo: false,
  hasAudio: false,
  hasScreen: false,
  videoInputs: [],
  audioInputs: [],
  audioOutputs: [],
  selectedVideoInput: null,
  selectedAudioInput: null,
  selectedAudioOutput: null,
  videoTiles: new Map(),
  localTileId: null,

  recoveryModalOpen: false,
  recoveryReason: 'unknown',
  screenShareInitiated: null,
  localMute: false,
  remoteMuteStatus: {},
  localVideoElement: null,
  attendees: {},
};

export default function streamReducer(state = initialState, action) {
  switch (action.type) {
    case INIT_DEVICES_SUCCESS: {
      const { videoInputs, audioInputs, audioOutputs } = action.payload;
      return {
        ...state,
        videoInputs,
        audioInputs,
        audioOutputs,
        selectedVideoInput: videoInputs[0]?.deviceId || null,
        selectedAudioInput: audioInputs[0]?.deviceId || null,
        selectedAudioOutput: audioOutputs[0]?.deviceId || null,
      };
    }

    case SET_SELECTED_DEVICE: {
      const { kind, deviceId } = action.payload;
      if (kind === 'video') {
        return { ...state, selectedVideoInput: deviceId };
      } else if (kind === 'audio') {
        return { ...state, selectedAudioInput: deviceId };
      } else if (kind === 'speaker') {
        return { ...state, selectedAudioOutput: deviceId };
      }
      return state;
    }

    case INIT_VIDEO_STREAM_SUCCESS:
      return {
        ...state,
        videoStream: action.payload,
        hasVideo: true,
        hasAudio: true,
        error: null,
      };
    case INIT_AUDIO_STREAM_SUCCESS:
      return {
        ...state,
        audioStream: action.payload,
        hasAudio: true,
        error: null,
      };
    case INIT_SCREEN_STREAM_SUCCESS:
      return {
        ...state,
        screenStream: action.payload,
        hasScreen: true,
        error: null,
      };
    case STOP_VIDEO_STREAM:
      return {
        ...state,
        videoStream: null,
        hasVideo: false,
      };
    case STOP_AUDIO_STREAM:
      return {
        ...state,
        audioStream: null,
        hasAudio: false,
      };
    case STOP_SCREEN_STREAM:
      return {
        ...state,
        screenStream: null,
        hasScreen: false,
      };
    case SET_DEVICE_CONTROLLER:
      return {
        ...state,
        deviceController: action.payload,
      };
    case SET_MEETING_SESSION:
      return {
        ...state,
        meetingSession: action.payload,
      };
    case STREAM_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case END_MEETING_SESSION:
      return {
        ...initialState,
      };
    case OPEN_MEDIA_RECOVERY_MODAL:
      return { ...state, recoveryModalOpen: true, recoveryReason: action.payload || 'unknown' };
    case CLOSE_MEDIA_RECOVERY_MODAL:
      return { ...state, recoveryModalOpen: false, recoveryReason: 'unknown' };
    case SET_SCREEN_SHARE_INITIATED:
      return { ...state, screenShareInitiated: action.payload };
    case CLEAR_SCREEN_SHARE_INITIATED:
      return { ...state, screenShareInitiated: null };
    case VIDEO_TILE_DID_UPDATE: {
      const { tileState } = action.payload;
      const newVideoTiles = new Map(state.videoTiles);
      newVideoTiles.set(tileState.tileId, tileState);
      return {
        ...state,
        videoTiles: newVideoTiles,
        localTileId: tileState.localTile ? tileState.tileId : state.localTileId,
      };
    }
    case VIDEO_TILE_WAS_REMOVED: {
      const { tileId } = action.payload;
      const newVideoTiles = new Map(state.videoTiles);
      newVideoTiles.delete(tileId);
      return {
        ...state,
        videoTiles: newVideoTiles,
        localTileId: tileId === state.localTileId ? null : state.localTileId,
      };
    }
    case SET_LOCAL_MUTE:
      return {
        ...state,
        localMute: action.payload,
      };
    case SET_REMOTE_MUTE:
      return {
        ...state,
        remoteMuteStatus: {
          ...state.remoteMuteStatus,
          [action.payload.attendeeId]: action.payload.muted,
        },
      };
    case SET_LOCAL_VIDEO_ELEMENT:
      return {
        ...state,
        localVideoElement: action.payload,
      };
    case SET_WEBRTC_STREAM:
      return {
        ...state,
        webrtcStream: action.payload,
      };
    case SET_WEBRTC_SESSION_ID:
      return {
        ...state,
        webrtcSessionId: action.payload,
      };
    case UPDATE_ATTENDEE:
      const { attendeeId, updates } = action.payload;
      if (!attendeeId) {
        console.warn('UPDATE_ATTENDEE called with null attendeeId');
        return state;
      }
      return {
        ...state,
        attendees: {
          ...state.attendees,
          [attendeeId]: {
            ...state.attendees[attendeeId],
            ...updates,
          },
        },
      };
    case REMOVE_ATTENDEE:
      return {
        ...state,
        attendees: (() => {
          const { [action.payload]: omitted, ...rest } = state.attendees;
          return rest;
        })(),
      };
    default:
      return state;
  }
}
